import { EltType } from 'legacy/lut/classes/models/partner360/ELT/EltType'
import {
  IEltDocFormData,
  IELTFormData,
  TEltFormData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import {
  businessPerformanceTabDoc,
  lastMeetingDataTabNumber,
  polyFinancialsTabNumber,
  scoreCardTabNumber,
} from 'legacy/lut/components/widgets/public/partner360/ELT/configs'
import { isDocType } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/isDocType'
import { useCallback, useEffect, useMemo } from 'react'

import { TUpdateELTFormData } from '../../../models/common'
import { IChangedSteps } from '../../../models/IChangedSteps'
import { getSteps } from '../header/helpers/getSteps'

import { checkTabEnabled } from './helpers/checkTabEnabled'
import { NavigationItem } from './NavigationItem'

import styles from './Navigation.module.scss'

interface IProps {
  formData: TEltFormData
  currentStep: number
  changedSteps: IChangedSteps
  updateData: TUpdateELTFormData
  eltType: EltType
  onTabToggleChange?: (pageId: string, value: boolean) => void
}

export const Navigation = ({
  eltType,
  currentStep,
  formData,
  updateData,
  changedSteps,
  onTabToggleChange,
}: IProps) => {
  const onStepChange = useCallback(
    (newStep: number) => () => {
      updateData('LastStep', newStep)
    },
    [updateData]
  )
  const pptxFormData = useMemo(() => {
    return formData as IELTFormData
  }, [formData])

  const docFormData = useMemo(() => {
    return formData as IEltDocFormData
  }, [formData])

  const getPages = useCallback(() => {
    const steps = getSteps(eltType)
    const keys = Object.keys(steps)

    if (isDocType(eltType)) {
      return keys.filter(
        (key) =>
          docFormData?.IsBusinessPerformanceTabEnabled || key !== String(businessPerformanceTabDoc)
      )
    }

    return keys
      .filter((key) => pptxFormData.LastMeetingData || key !== String(lastMeetingDataTabNumber))
      .filter((key) => {
        return !(
          !pptxFormData?.AdditionalSections?.RevenuePoly && key === String(polyFinancialsTabNumber)
        )
      })
      .filter((key) => {
        return !(!pptxFormData?.PLC?.length && key === String(scoreCardTabNumber))
      })
  }, [
    eltType,
    pptxFormData.LastMeetingData,
    pptxFormData.AdditionalSections,
    pptxFormData.PLC,
    docFormData.IsBusinessPerformanceTabEnabled,
  ])

  const pages = useMemo(() => {
    const steps = getSteps(eltType)
    return getPages().map((key) => ({
      id: steps[key].title,
      title: steps[key].navigationTitle || '',
      order: Number(key),
      subtitle: steps[key].navigationSubTitle,
      isToggleable: steps[key].isToggleable,
    }))
  }, [eltType, getPages])

  useEffect(() => {
    const stepExists = pages.some((el) => el.order === currentStep)

    if (!stepExists && pages.length > 0) {
      onStepChange(pages[0].order)()
    }
  }, [currentStep, onStepChange, pages])

  return (
    <div className={styles.container}>
      <div className={styles.breadcrumb}>
        {pages.map((page) => {
          const isTabEnabled = page.isToggleable ? checkTabEnabled(page.id, formData) : false
          return (
            <NavigationItem
              key={page.order}
              id={page.id}
              isChanged={changedSteps[page.order]}
              isActive={page.order === currentStep}
              title={page.title}
              onStepChange={onStepChange(page.order)}
              subTitle={page.subtitle}
              isToggleable={page.isToggleable}
              isEnabled={isTabEnabled}
              onTabToggleChange={onTabToggleChange}
            />
          )
        })}
      </div>
    </div>
  )
}
